// TatianaVoice.ts — Elegant Local TTS Singleton
//
// VOICE PROFILE: Female, elegant, ancient tone
// PARAMETERS: pitch 0.85 (deeper/stoic), rate 0.9 (deliberate/authoritative)
// CLEANING: Strip JSON code blocks, tool_call blocks, markdown artifacts before synthesis
// SYNC: Only speak when explicitly called (after first token received)

class TatianaVoice {
  private static instance: TatianaVoice;
  private synth: SpeechSynthesis;
  private voice: SpeechSynthesisVoice | null = null;
  private speaking: boolean = false;
  private enabled: boolean = false;
  private utteranceQueue: string[] = [];
  private voiceLoaded: boolean = false;

  private constructor() {
    this.synth = globalThis.speechSynthesis;
    this.loadVoice();

    // Voices may load asynchronously — listen for the voiceschanged event
    if (this.synth.onvoiceschanged !== undefined) {
      this.synth.addEventListener('voiceschanged', () => {
        this.loadVoice();
      });
    }
  }

  /**
   * Voice selection priority:
   * 1. 'Google UK English Female'
   * 2. Any voice with 'female' in name + lang starts with 'en'
   * 3. Any voice with lang 'en-GB'
   * 4. First English voice available
   */
  private loadVoice(): void {
    const voices = this.synth.getVoices();
    if (voices.length === 0) return;

    // Priority 1: Google UK English Female
    let selected = voices.find(v => v.name === 'Google UK English Female');

    // Priority 2: Any female English voice
    if (!selected) {
      selected = voices.find(
        v => v.name.toLowerCase().includes('female') && v.lang.startsWith('en')
      );
    }

    // Priority 3: en-GB voice
    if (!selected) {
      selected = voices.find(v => v.lang === 'en-GB');
    }

    // Priority 4: First English voice
    if (!selected) {
      selected = voices.find(v => v.lang.startsWith('en'));
    }

    // Fallback: first available
    if (!selected && voices.length > 0) {
      selected = voices[0];
    }

    if (selected) {
      this.voice = selected;
      this.voiceLoaded = true;
    }
  }

  static getInstance(): TatianaVoice {
    if (!TatianaVoice.instance) {
      TatianaVoice.instance = new TatianaVoice();
    }
    return TatianaVoice.instance;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
    this.stop();
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.stop();
    }
    return this.enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getIsSpeaking(): boolean {
    return this.speaking;
  }

  /**
   * Text cleaning pipeline:
   * 1. Remove ```json ... ``` blocks
   * 2. Remove ```tool_call ... ``` blocks
   * 3. Remove any remaining ``` blocks
   * 4. Remove markdown ** and * formatting
   * 5. Remove [UPLINK] / [NEURAL] / [AETHER] prefixes
   * 6. Trim whitespace
   */
  private cleanText(text: string): string {
    let cleaned = text;

    // 1. Remove ```json ... ``` blocks
    cleaned = cleaned.replace(/```json[\s\S]*?```/gi, '');

    // 2. Remove ```tool_call ... ``` blocks
    cleaned = cleaned.replace(/```tool_call[\s\S]*?```/gi, '');

    // 3. Remove any remaining ``` blocks
    cleaned = cleaned.replace(/```[\s\S]*?```/g, '');

    // 4. Remove markdown bold and italic
    cleaned = cleaned.replace(/\*\*([^*]*)\*\*/g, '$1');
    cleaned = cleaned.replace(/\*([^*]*)\*/g, '$1');

    // 5. Remove system prefixes
    cleaned = cleaned.replace(/\[\s*UPLINK\s*\]/gi, '');
    cleaned = cleaned.replace(/\[\s*NEURAL\s*\]/gi, '');
    cleaned = cleaned.replace(/\[\s*AETHER\s*\]/gi, '');
    cleaned = cleaned.replace(/\[\s*SENSOR\s*\]/gi, '');
    cleaned = cleaned.replace(/\[\s*SYSTEM\s*\]/gi, '');
    cleaned = cleaned.replace(/\[\s*EXECUTING\s*\]/gi, '');

    // 6. Remove JSON objects that might be inline
    cleaned = cleaned.replace(/\{[^{}]*"action"\s*:\s*"[^"]*"[^{}]*\}/g, '');

    // 7. Trim whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    return cleaned;
  }

  speak(text: string): void {
    if (!this.enabled) return;
    if (!this.synth) return;

    const cleaned = this.cleanText(text);
    if (!cleaned) return;

    // If currently speaking, queue
    if (this.speaking) {
      this.utteranceQueue.push(cleaned);
      return;
    }

    this.synthesize(cleaned);
  }

  private synthesize(text: string): void {
    // Ensure voice is loaded
    if (!this.voiceLoaded) {
      this.loadVoice();
    }

    const utterance = new SpeechSynthesisUtterance(text);

    if (this.voice) {
      utterance.voice = this.voice;
    }

    // Elegant voice profile: deeper pitch, deliberate rate
    utterance.pitch = 0.85;
    utterance.rate = 0.9;
    utterance.volume = 1.0;

    utterance.onstart = () => {
      this.speaking = true;
    };

    utterance.onend = () => {
      this.speaking = false;
      this.processQueue();
    };

    utterance.onerror = () => {
      this.speaking = false;
      this.processQueue();
    };

    this.synth.speak(utterance);
    this.speaking = true;
  }

  private processQueue(): void {
    if (this.utteranceQueue.length > 0 && this.enabled) {
      const next = this.utteranceQueue.shift();
      if (next) {
        this.synthesize(next);
      }
    }
  }

  stop(): void {
    this.utteranceQueue = [];
    if (this.synth) {
      this.synth.cancel();
    }
    this.speaking = false;
  }
}

export const tatianaVoice = TatianaVoice.getInstance();
export default TatianaVoice;
